/** Worker pool that renders whole display frames (packets may differ in length). */

interface PendingJob {
  resolve: (image: ImageBitmap) => void;
  reject: (error: Error) => void;
}

interface WorkerReply {
  type: 'rendered' | 'error';
  bitmap?: ImageBitmap;
  buffer?: ArrayBuffer;
  width?: number;
  height?: number;
  message?: string;
  jobId: number;
}

export class FrameRenderPool {
  private readonly workers: Worker[] = [];
  private readonly pending = new Map<number, PendingJob>();
  private nextWorker = 0;
  private nextJobId = 1;
  private terminated = false;

  constructor(size: number) {
    const count = Math.max(1, Math.floor(size));
    for (let i = 0; i < count; i++) {
      const worker = new Worker(new URL('./render.worker.ts', import.meta.url), { type: 'module' });
      worker.onmessage = (event: MessageEvent<WorkerReply>) => this.onMessage(event.data);
      worker.onerror = (event) => this.failAll(new Error(event.message || 'render worker failed'));
      this.workers.push(worker);
    }
  }

  render(
    packets: Uint8Array[],
    version: number,
    ecc: number,
    scale: number,
    columns: number,
  ): Promise<ImageBitmap> {
    if (this.terminated) return Promise.reject(new Error('render pool terminated'));
    const worker = this.workers[this.nextWorker];
    if (!worker || packets.length === 0) {
      return Promise.reject(new Error('render pool has no worker or empty frame'));
    }
    this.nextWorker = (this.nextWorker + 1) % this.workers.length;

    let total = 0;
    for (const packet of packets) total += packet.length;
    const buffer = new Uint8Array(total);
    const offsets = new Uint32Array(packets.length + 1);
    let offset = 0;
    packets.forEach((packet, index) => {
      buffer.set(packet, offset);
      offset += packet.length;
      offsets[index + 1] = offset;
    });

    const jobId = this.nextJobId++;
    return new Promise<ImageBitmap>((resolve, reject) => {
      this.pending.set(jobId, { resolve, reject });
      worker.postMessage(
        {
          type: 'render',
          buffer: buffer.buffer,
          offsets: offsets.buffer,
          version,
          ecc,
          scale,
          columns,
          jobId,
        },
        [buffer.buffer, offsets.buffer],
      );
    });
  }

  terminate(): void {
    if (this.terminated) return;
    this.terminated = true;
    for (const worker of this.workers) worker.terminate();
    this.workers.length = 0;
    this.failAll(new Error('render pool terminated'));
  }

  private onMessage(msg: WorkerReply): void {
    const job = this.pending.get(msg.jobId);
    if (!job) return;
    this.pending.delete(msg.jobId);

    if (msg.type === 'error') {
      job.reject(new Error(msg.message || 'render failed'));
      return;
    }
    if (msg.bitmap) {
      job.resolve(msg.bitmap);
      return;
    }
    if (msg.buffer) {
      const data = new Uint8ClampedArray(msg.buffer);
      void createImageBitmap(new ImageData(data, msg.width ?? 0, msg.height ?? 0))
        .then((bitmap) => job.resolve(bitmap))
        .catch((err) => job.reject(err instanceof Error ? err : new Error(String(err))));
      return;
    }
    job.reject(new Error('render worker returned nothing'));
  }

  private failAll(error: Error): void {
    for (const job of this.pending.values()) job.reject(error);
    this.pending.clear();
  }
}
